import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { SOCKET_URL, GAME_DURATION_MS } from '../config';

interface LayoutProps {
  children: React.ReactNode;
  showGameNav?: boolean;
}

interface GameData {
  id: string;
  status: 'live' | 'completed';
  currentPossession: number;
  totalPossessions: number;
  startTime?: string;
  endTime?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, showGameNav = false }) => {
  const [gameProgress, setGameProgress] = useState<number>(0);
  const [gameStatus, setGameStatus] = useState<string>('waiting');
  const [gameStartTime, setGameStartTime] = useState<Date | null>(null);
  const [gameEndTime, setGameEndTime] = useState<Date | null>(null);
  const [disableTransition, setDisableTransition] = useState<boolean>(false);
  const previousProgress = useRef<number>(0);

  // Helper function to calculate progress from start/end times
  const calculateProgress = useCallback((start: Date, end: Date | null): number => {
    const now = Date.now();
    const startTime = start.getTime();
    
    let duration: number;
    if (end) {
      duration = end.getTime() - startTime;
    } else {
      duration = GAME_DURATION_MS;
    }

    const elapsed = now - startTime;
    return Math.min(100, Math.max(0, (elapsed / duration) * 100));
  }, []);

  useEffect(() => {
    // Connect to Socket.IO for game updates
    // Always maintain connection regardless of showGameNav to preserve state across pages
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      setGameStatus('connected');
    });

    newSocket.on('disconnect', () => {
      setGameStatus('disconnected');
    });

    // Listen for game updates to extract time information
    newSocket.on('continuous-game-update', (data: any) => {
      const { game } = data;
      if (game && game.status === 'live') {
        if (game.startTime) {
          const start = new Date(game.startTime);
          setGameStartTime(start);
          
          let end: Date | null = null;
          if (game.endTime) {
            end = new Date(game.endTime);
            setGameEndTime(end);
          } else {
            // Calculate end time from start time + duration if endTime not available
            end = new Date(start.getTime() + GAME_DURATION_MS);
            setGameEndTime(end);
          }
          
          // Calculate progress immediately to avoid animation from 0
          const progress = calculateProgress(start, end);
          // If jumping from 0 to a significant value, disable transition
          if (previousProgress.current === 0 && progress > 5) {
            setDisableTransition(true);
            // Re-enable transition after a brief delay to allow the initial render to complete
            setTimeout(() => setDisableTransition(false), 50);
          }
          previousProgress.current = progress;
          setGameProgress(progress);
        }
        setGameStatus('live');
      }
    });

    newSocket.on('continuous-game-start', (data: any) => {
      const { game } = data;
      if (game && game.startTime) {
        const start = new Date(game.startTime);
        setGameStartTime(start);
        // Calculate end time from start time + duration
        setGameEndTime(new Date(start.getTime() + GAME_DURATION_MS));
      }
      previousProgress.current = 0;
      setGameProgress(0);
      setGameStatus('starting');
    });

    newSocket.on('continuous-game-complete', (data: any) => {
      const { game } = data;
      if (game && game.endTime) {
        setGameEndTime(new Date(game.endTime));
      }
      setGameProgress(100);
      setGameStatus('completed');
      
      // Reset after a delay for next game
      setTimeout(() => {
        previousProgress.current = 0;
        setGameProgress(0);
        setGameStatus('waiting');
        setGameStartTime(null);
        setGameEndTime(null);
      }, 5000);
    });

    newSocket.on('continuous-games-current', (data: any) => {
      const { activeGames } = data;
      if (activeGames && activeGames.length > 0) {
        // Use the first active game for progress calculation
        const activeGame = activeGames.find((game: GameData | null) => game !== null);
        if (activeGame) {
          if (activeGame.startTime) {
            const start = new Date(activeGame.startTime);
            setGameStartTime(start);
            
            let end: Date | null = null;
            if (activeGame.endTime) {
              end = new Date(activeGame.endTime);
              setGameEndTime(end);
            } else {
              // Calculate end time from start time + duration if endTime not available
              end = new Date(start.getTime() + GAME_DURATION_MS);
              setGameEndTime(end);
            }
            
            // Calculate progress immediately to avoid animation from 0
            if (activeGame.status === 'live') {
              const progress = calculateProgress(start, end);
              // If jumping from 0 to a significant value, disable transition
              if (previousProgress.current === 0 && progress > 5) {
                setDisableTransition(true);
                // Re-enable transition after a brief delay to allow the initial render to complete
                setTimeout(() => setDisableTransition(false), 50);
              }
              previousProgress.current = progress;
              setGameProgress(progress);
            }
          }
          setGameStatus(activeGame.status);
        }
      }
    });

    return () => {
      newSocket.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array - calculateProgress is stable useCallback

  // Smooth timer-based progress update
  useEffect(() => {
    // Only run timer when we have a start time and game is live or starting
    if (!gameStartTime || (gameStatus !== 'live' && gameStatus !== 'starting')) {
      return;
    }

    const updateProgress = () => {
      if (!gameStartTime) {
        setGameProgress(0);
        return;
      }

      const now = Date.now();
      const start = gameStartTime.getTime();
      
      // Calculate duration: use endTime if available, otherwise use startTime + GAME_DURATION_MS
      let duration: number;
      if (gameEndTime) {
        duration = gameEndTime.getTime() - start;
      } else {
        duration = GAME_DURATION_MS;
      }

      const elapsed = now - start;
      const progress = Math.min(100, Math.max(0, (elapsed / duration) * 100));
      previousProgress.current = progress;
      setGameProgress(progress);

      // If game is complete, stop the interval
      if (progress >= 100) {
        setGameProgress(100);
      }
    };

    // Update immediately
    updateProgress();

    // Update every 100ms for smooth animation
    const interval = setInterval(updateProgress, 100);

    return () => clearInterval(interval);
  }, [gameStartTime, gameEndTime, gameStatus]);

  // Helper to get next scheduled time in CST (:00 or :30)
  const getNextScheduledTime = (addMinutes: number = 0): Date => {
    const now = new Date();
    const cstFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const cstParts = cstFormatter.formatToParts(now);
    const cstYear = parseInt(cstParts.find(p => p.type === 'year')?.value || '0');
    const cstMonth = parseInt(cstParts.find(p => p.type === 'month')?.value || '1') - 1;
    const cstDay = parseInt(cstParts.find(p => p.type === 'day')?.value || '1');
    const cstHour = parseInt(cstParts.find(p => p.type === 'hour')?.value || '0');
    const cstMinute = parseInt(cstParts.find(p => p.type === 'minute')?.value || '0');
    
    // Find next :00 or :30
    let nextMinute;
    let nextHour = cstHour;
    let nextDay = cstDay;
    let nextMonth = cstMonth;
    let nextYear = cstYear;
    
    if (cstMinute < 30) {
      nextMinute = 30;
    } else {
      nextMinute = 0;
      nextHour = (cstHour + 1) % 24;
      if (nextHour === 0) {
        nextDay++;
      }
    }
    
    // Add any additional minutes (e.g., for end time)
    nextMinute += addMinutes;
    if (nextMinute >= 60) {
      nextMinute -= 60;
      nextHour = (nextHour + 1) % 24;
      if (nextHour === 0) {
        nextDay++;
      }
    }
    
    // Create date string in CST and convert to UTC Date
    // Use a workaround: create date in local timezone that represents CST time
    const dateStr = `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(nextDay).padStart(2, '0')}T${String(nextHour).padStart(2, '0')}:${String(nextMinute).padStart(2, '0')}:00`;
    
    // Try to create a date that when formatted in CST gives us the right time
    // We'll use a binary search approach: try different UTC times until formatting matches
    let testDate = new Date(dateStr);
    let attempts = 0;
    while (attempts < 20) {
      const testParts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(testDate);
      
      const testHour = parseInt(testParts.find(p => p.type === 'hour')?.value || '0');
      const testMinute = parseInt(testParts.find(p => p.type === 'minute')?.value || '0');
      
      if (testHour === nextHour && testMinute === nextMinute) {
        return testDate;
      }
      
      // Adjust: calculate difference and move forward/backward
      const testMinutes = testHour * 60 + testMinute;
      const targetMinutes = nextHour * 60 + nextMinute;
      let diff = targetMinutes - testMinutes;
      if (diff > 12 * 60) diff -= 24 * 60;
      if (diff < -12 * 60) diff += 24 * 60;
      
      testDate = new Date(testDate.getTime() + diff * 60 * 1000);
      attempts++;
    }
    
    return testDate;
  };

  // Get start time for display
  // When live/starting: show game start time
  // When waiting/standby: show next scheduled start time (:00 or :30)
  const getCurrentTime = () => {
    // If we have a game start time, use it
    if (gameStartTime && (gameStatus === 'live' || gameStatus === 'starting')) {
      return gameStartTime.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        timeZone: 'America/Chicago'
      });
    }
    
    // Otherwise, calculate next scheduled start time (:00 or :30)
    const nextScheduled = getNextScheduledTime(0);
    return nextScheduled.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      timeZone: 'America/Chicago'
    });
  };

  // Calculate end time (games run for 15 minutes, scheduled at :00 and :30)
  // When live/starting: show game end time
  // When waiting/standby: show next scheduled end time (:00 or :30 + 15 minutes)
  const getEndTime = () => {
    // Use the actual game end time if available and game is live/starting
    if (gameEndTime && (gameStatus === 'live' || gameStatus === 'starting')) {
      return gameEndTime.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        timeZone: 'America/Chicago'
      });
    }
    
    // Otherwise, calculate next :00 or :30 + 15 minutes (upcoming cycle end)
    const nextScheduledEnd = getNextScheduledTime(15);
    return nextScheduledEnd.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      timeZone: 'America/Chicago'
    });
  };

  const getStatusDisplay = () => {
    switch (gameStatus) {
      case 'live': return 'LIVE GAMES';
      case 'starting': return 'GAMES STARTING';
      case 'completed': return 'GAMES COMPLETED';
      case 'waiting': return 'NEXT GAMES';
      default: return 'STANDBY';
    }
  };

  return (
    <>
      {/* Exterior Border Decorations */}
      <div className="container--exterior">
        <div className="exterior-border left">
          <div className="border-box top-left"></div>
          <div className="border-text left"><span>&#47;&#47;</span> Remember: gamble in moderation <span>&#47;&#47;</span></div>
          <div className="border-box bottom-left"></div>
        </div>
        <div className="exterior-border right">
          <div className="border-box top-right"></div>
          <div className="border-text right"><span>&#47;&#47;</span> brought to you by neo vegas Corp trust <span>&#47;&#47;</span></div>
          <div className="border-box bottom-right"></div>
        </div>
      </div>

      {/* Main Page Content */}
      <div id="top" className="page-wrapper">
        {showGameNav && (
          <div className="nav games">
            <div className="interior-nav--hero">
              <svg viewBox="0 0 200 40" preserveAspectRatio="none" className="diagonal">
                <rect x="0.5" y="0.5" width="199" height="39" fill="none" stroke="#666" strokeWidth="1" vectorEffect="non-scaling-stroke"></rect>
                <line x1="200" y1="0" x2="0" y2="40" stroke="#666" strokeWidth="1" vectorEffect="non-scaling-stroke"></line>
              </svg>
              <div className="div-block-4">
                <img src="/images/league.svg" loading="lazy" alt="" />
                <div className="overline">Season 2</div>
              </div>
              <svg viewBox="0 0 200 40" preserveAspectRatio="none" className="diagonal">
                <rect x="0.5" y="0.5" width="199" height="39" fill="none" stroke="#666" strokeWidth="1" vectorEffect="non-scaling-stroke"></rect>
                <line x1="200" y1="0" x2="0" y2="40" stroke="#666" strokeWidth="1" vectorEffect="non-scaling-stroke"></line>
              </svg>
            </div>
            <div className="gametime-wrapper">
              <div className="gametime-status">{getStatusDisplay()} - {getCurrentTime()} CST</div>
              <div className="gametimer">
                <div 
                  className="gametime-progress"
                  style={{ 
                    width: `${gameProgress}%`,
                    transition: disableTransition ? 'none' : 'width 0.5s ease-out',
                    backgroundColor: '#ffffff'
                  }}
                ></div>
              </div>
              <div className="gametime-end">Cycle End {getEndTime()} CST</div>
            </div>
          </div>
        )}
        
        {children}
      </div>
    </>
  );
};

export default Layout; 